import {
  addressMap,
  CSTTestnet,
  DAITestnet,
  SPONMainnet,
  SPONTestnet,
  USDCTestnet,
  USDTTestnet,
  USPONMainnet,
  USPONTestnet,
  WETHTestnet,
} from "@/contracts/addresses";
import USDTIcon from "@spheron/ui-library/dist/assets/crypto-social/Tether.svg?url";
import USDCIcon from "@spheron/ui-library/dist/assets/crypto-social/Usdc.svg?url";
import DaiIcon from "@spheron/ui-library/dist/assets/crypto-social/Dai.svg?url";
import WethIcon from "@spheron/ui-library/dist/assets/crypto-social/Weth.svg?url";
import { defineChain } from "viem";
import { IOptions } from "@spheron/ui-library";
import { createThirdwebClient } from "thirdweb";

// Testnet URLs
export const SERVICE_API_URL = "https://api.spheron.network";

export const WEBSOCKET_PROVIDER_URL =
  "wss://base-sepolia.g.alchemy.com/v2/vN6ZeMQVQEjbGURcN5_vD_oVFQ_u6pQ9";

export const SPHERON_RPC_URL =
  "https://base-sepolia.g.alchemy.com/v2/vN6ZeMQVQEjbGURcN5_vD_oVFQ_u6pQ9";

export const SPHERON_RPC_MAINNET_URL =
  "https://base-mainnet.g.alchemy.com/v2/uYxTMhdvA7DI3lBT1UJp2";

export const WEBSOCKET_PROVIDER_MAINNET_URL =
  "wss://base-mainnet.g.alchemy.com/v2/uYxTMhdvA7DI3lBT1UJp2";

export const RPC_MAP: Record<NetworkType, string> = {
  testnet: SPHERON_RPC_URL,
  mainnet: SPHERON_RPC_MAINNET_URL,
};

export const WEBSOCKET_PROVIDER_MAP: Record<NetworkType, string> = {
  testnet: WEBSOCKET_PROVIDER_URL,
  mainnet: WEBSOCKET_PROVIDER_MAINNET_URL,
};

export const rpcProvider = {
  testnet: {
    HTTP_URL: SPHERON_RPC_URL,
    WSS_URL: WEBSOCKET_PROVIDER_URL,
  },
  mainnet: {
    HTTP_URL: SPHERON_RPC_MAINNET_URL,
    WSS_URL: WEBSOCKET_PROVIDER_MAINNET_URL,
  },
};

export const BASE_MAINNET_PUBLIC_RPC_HTTP_URL = "https://mainnet.base.org";

export const SPHERON_MODEL_BASE_URL = "https://api.spheron.ai/v1";

export const SPHERON_MODEL_CHAT_PROXY_URL =
  "https://api.spheron.ai/v1/chat/completions";

export const SPHERON_MODEL_TAGS_URL = "https://api.spheron.ai/api/tags";

const getBaseUrl = () => {
  if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") {
    return "https://console.spheron.network";
  }
  if (process.env.NEXT_PUBLIC_VERCEL_ENV === "preview") {
    return `https://${process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL}`;
  }
  return "http://localhost:3009";
};

export const BASE_URL = getBaseUrl();

export const PROXY_URL =
  process.env.NODE_ENV === "production"
    ? "https://provider-proxy.spheron.network"
    : "https://provider-proxy.spheron.network";

export const PROVIDER_PROXY_WS_URL =
  process.env.NODE_ENV === "production"
    ? "wss://provider-proxy.spheron.network"
    : "wss://provider-proxy.spheron.network";

export const CONSOLE_MINIMUM_DEPOSIT =
  process.env.NEXT_PUBLIC_VERCEL_ENV === "production" ? 15 : 1;

export const CONSOLE_SPON_MINIMUM_DEPOSIT = 20;

export const PRIVY_APP_ID = process.env.NEXT_PUBLIC_PRIVY_APP_ID || "";
export const PRIVY_APP_SECRET = process.env.PRIVY_APP_SECRET || "";
export const PRIVY_CLIENT_ID = process.env.NEXT_PUBLIC_PRIVY_CLIENT_ID || "";

export interface IToken {
  id: number;
  name: string;
  symbol: string;
  decimal: number;
  address: string | `0x${string}`;
  logo: any;
  minDeposit?: number;
}

export interface INetwork {
  chainId: number;
  chainName: string;
  rpcUrls: string[];
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  blockExplorerUrls: string[];
}

export type NetworkType = "testnet" | "mainnet";

export const networkMap: Record<NetworkType, INetwork> = {
  testnet: {
    chainId: 84532,
    chainName: "Base Sepolia",
    rpcUrls: [SPHERON_RPC_URL],
    nativeCurrency: {
      name: "Sepolia Ether",
      symbol: "ETH",
      decimals: 18,
    },
    blockExplorerUrls: ["https://sepolia.basescan.org/"],
  },
  mainnet: {
    chainId: 8453,
    chainName: "Base Mainnet",
    rpcUrls: [SPHERON_RPC_MAINNET_URL],
    nativeCurrency: {
      name: "Ether",
      symbol: "ETH",
      decimals: 18,
    },
    blockExplorerUrls: ["https://basescan.org/"],
  },
};

// TODO: getAllTokens contract call
export const tokenMap: Record<NetworkType, IToken[]> = {
  testnet: [
    {
      id: 1,
      name: "Tether USD Test Token",
      symbol: "USDT",
      decimal: 6,
      address: USDTTestnet,
      logo: USDTIcon,
      minDeposit: 15,
    },
    {
      id: 2,
      name: "DAI Test Token",
      symbol: "DAI",
      decimal: 18,
      address: DAITestnet,
      logo: DaiIcon,
      minDeposit: 15,
    },
    {
      id: 3,
      name: "Test Token USD Coin",
      symbol: "USDC",
      decimal: 6,
      address: USDCTestnet,
      logo: USDCIcon,
      minDeposit: 15,
    },
    {
      id: 4,
      name: "Wrapped ETH Test Token",
      symbol: "WETH",
      decimal: 18,
      address: WETHTestnet,
      logo: WethIcon,
      minDeposit: 0.005,
    },
    {
      id: 5,
      name: "Credit Token",
      symbol: "CST",
      decimal: 6,
      address: CSTTestnet,
      logo: WethIcon,
      minDeposit: 15,
    },
    {
      id: 6,
      name: "Spheron Test Token",
      symbol: "uSPON",
      decimal: 6,
      address: USPONTestnet,
      logo: WethIcon,
      minDeposit: 15,
    },
    {
      id: 7,
      name: "Spheron Testnet Token",
      symbol: "SPON",
      decimal: 18,
      address: SPONTestnet,
      logo: WethIcon,
      minDeposit: 0,
    },
  ],
  mainnet: [
    {
      id: 3,
      name: "Spheron Test Token",
      symbol: "uSPON",
      decimal: 6,
      address: USPONMainnet,
      logo: USDCIcon,
      minDeposit: 15,
    },
    {
      id: 4,
      name: "Spheron Token",
      symbol: "SPON",
      decimal: 18,
      address: SPONMainnet,
      logo: USDCIcon,
    },
  ],
};

export const networkType =
  (process.env.NEXT_PUBLIC_NETWORK_TYPE as string) || "mainnet";

export enum Tier {
  One,
  Two,
  Three,
  Four,
  Five,
  Six,
  Seven,
}

let tiers = [
  Tier.One,
  Tier.Two,
  Tier.Three,
  Tier.Four,
  Tier.Five,
  Tier.Six,
  Tier.Seven,
];

export const validTiers: { [x: string]: Tier[] } = {
  secured1: [Tier.One],
  secured2: [Tier.Two],
  secured3: [Tier.Three],
  community1: [Tier.Four],
  community2: [Tier.Five],
  community3: [Tier.Six],
  community4: [Tier.Seven],
  secured: [Tier.One, Tier.Two, Tier.Three],
  community: [Tier.Four, Tier.Five, Tier.Six, Tier.Seven],
  "community-default": [
    Tier.One,
    Tier.Two,
    Tier.Three,
    Tier.Four,
    Tier.Five,
    Tier.Six,
    Tier.Seven,
  ],
};

export const LEASES_PAGE_SIZE = 10;
export const MINIMUM_TOPUP_AMOUNT: Record<string, number> = {
  [USDTTestnet]: 15,
  [USDCTestnet]: 15,
  [DAITestnet]: 15,
  [WETHTestnet]: 0.005,
};
export const MINIMUM_WITHDRAW_AMOUNT = 0;

export const ORDER_UPDATE_ACCEPTED_TIMEOUT = 60000 * 2;

export const ORDER_CREATED_TIMEOUT = 60000;

export const TOKEN_EXPIRY_TIME = 60 * 60 * 6 * 1000;

export const THIRD_WEB_CLIENT_ID =
  process.env.THIRD_WEB_CLIENT_ID || "11fb2288161d8c2fb74666e399b9ac58";

export const MARKETPLACE_MAX_CPU = 24;
export const MARKETPLACE_MAX_CPU_PER_GPU = 8;
export const MARKETPLACE_MAX_STORAGE = 500;
export const MARKETPLACE_MAX_RAM = 36;
export const MARKETPLACE_MAX_RAM_PER_GPU = 32;

export const MIN_FIZZ_RESOURCE_UNITS = {
  cpu: 4,
  ram: 8,
  storage: 100,
} as const;

export const FIZZ_SCRIPT_LATEST_VERSION = "v1.6.0";
export const FIZZ_LATEST_VERSION = "v1.4.0";
export const DISABLE_DEPLOYMENT_AND_DEPOSIT =
  process.env.NEXT_PUBLIC_DISABLE_DEPLOYMENT_AND_DEPOSIT === "true";

export const MODEL_MAINTENANCE_MODE =
  process.env.NEXT_PUBLIC_MODEL_MAINTENANCE_MODE === "true";

const network = networkMap[networkType as NetworkType];
export const chain = defineChain({
  id: network.chainId,
  name: network.chainName,
  network: network.chainName,

  nativeCurrency: {
    decimals: network.nativeCurrency.decimals,
    name: network.nativeCurrency.name,
    symbol: network.nativeCurrency.symbol,
  },
  rpcUrls: {
    default: {
      http: network.rpcUrls,
    },
  },
  blockExplorers: {
    default: {
      name: "Explorer",
      url: network.blockExplorerUrls[0],
    },
  },
});
export const PRIVY_WEBHOOK_SIGNING_KEY =
  process.env.PRIVY_WEBHOOK_SIGNING_KEY || "";

export const SIGNATURE_DEADLINE =
  Number(process.env.SIGNATURE_DEADLINE) || 86_400;

export const EXCLUDED_PROVIDERS_HOST_URIS = [
  "localhost",
  "provider.gpu.gpufarm.xyz",
  "provider.supergpu.xyz",
  "provider.devnetdsphn.com",
  "23.150.248.152",
];

export const ALWAYS_RENTED_PROVIDERS = [
  "0x6c6F67927c0953bF2994D6133f5B177cDE7f74BD",
  "0x569FFbc1F9EeDdEF8E0b54b9A0cacf31c8f74763",
  "0x37F83E51cc2719D9aceD750f03fcd54585017d39",
  "0x9d402D3A6d8b678cc67E4f395Ed54CdCC9C24963",
  "0xa2bBa7C86D351070F53Af1DC55F2286F151d5c72",
  "0x9eC1B4b7D5a63F8Ee42C713279262db12873be15",
  "0xc3f69b9Cc5aF76B5ACAcCA23c465fa5e6A37b905",
  "0x9fa83fdb5de110f2d7c4684b508cff36d56b436b",
  "0x2fbe03bab1644fc42e3043ee2083f99f5756d352",
  "0x1a95Ff8ADE2Cd6F42e015BF1b3D3e3aA7512B7D2",
  "0x3D86D3Ec1f3E95977c75E919C793F6A625454c7d",
  "0x2e530b728cd25856Bf88D1DB6E22527e957B861f",
  "0xeaFb59E1B247f4d83c1DbDBc4B957387Cc565f5C",
  "0x960F85b570e8EE7b7Da27Ed5b768B43B43779C54",
].map((address) => address.toLowerCase());

export const MODEL_MAC_FILTER_THRESHOLDS = {
  cpuCores: 8,
  ram: 16,
  storage: 100,
};

export const thirdwebClient = createThirdwebClient({
  clientId: THIRD_WEB_CLIENT_ID,
});

export const DISABLE_SPON = process.env.NEXT_PUBLIC_DISABLE_SPON === "true";

export const gpuDataDelay = parseInt(
  process.env.NEXT_PUBLIC_GPU_CRON_DATA_DELAY || "120000"
);

export const LIVE_PRICE_UPDATE_INTERVAL = 1000 * 60 * 5;
